/**
 * Generic Composable Forms — pure normalizer.
 *
 * Turns a control_form-shaped object (`ui_config` + `fields`) into an ORDERED
 * list of presentation-agnostic `Block`s. Both the web and the mobile generic
 * renderers consume this; the mobile mirror MUST produce IDENTICAL output, so
 * keep this file free of Angular/DOM imports and side effects.
 *
 * `ui_config.sections[]` is the ordered spine. Each entry resolves to exactly one
 * block, in precedence order: QUESTIONS (form.fields tagged with the section) ->
 * TABLE (ui_config.lines.fields tagged with it) -> HEADER GROUP
 * (ui_config.header.fields tagged with it). Header fields with no section_name
 * stay in the single leading header block. `ui_config.footer.fields` become one
 * flat trailing block (no grouping).
 */

export type Presentation = 'dropdown' | 'accordion' | 'inline';

export interface Field {
  id: string | number;
  name: string;
  label?: string;
  type: string;
  is_optional?: boolean;
  ui_config?: any;
  [k: string]: any;
}

/**
 * `instructions` is guidance text ("how do I fill this in"), distinct from the
 * heading `label` ("what is this"). Labels double as identifiers — the table
 * accordion heading and PDF/CSV column headers reuse them — so guidance must not
 * be crammed into a label. Section-derived blocks carry `section.instructions`;
 * every other block carries null (explicit-null convention, like sectionName /
 * label). Field-level guidance lives on `field.ui_config.instructions` and needs
 * no normalizer support: blocks already carry whole field objects.
 */
export type Block =
  | {
      kind: 'header';
      sectionName: string | null;
      label: string | null;
      instructions: string | null;
      fields: Field[];
    }
  | {
      kind: 'questions';
      sectionName: string | null;
      label: string | null;
      instructions: string | null;
      presentation: Presentation;
      responseOptions: string[];
      questions: Field[];
    }
  | {
      kind: 'table';
      sectionName: string | null;
      label: string | null;
      instructions: string | null;
      presentation: Presentation;
      columns: Field[];
    }
  | { kind: 'signatures'; roles: string[]; instructions: string | null }
  | {
      kind: 'footer';
      sectionName: string | null;
      label: string | null;
      instructions: string | null;
      fields: Field[];
    };

/**
 * ---- Saved-line rehydration (pure) ----
 *
 * `report_form_line` rows are storage-shaped: `{ id, comment, field_values:
 * [{ id, value }], pictures, videos }`, where each `field_values[].id` is a FIELD
 * id. Table rows and question answers coexist in that one collection and are told
 * apart ONLY by field id: a table row carries the block's COLUMN ids, a question
 * answer carries a custom_field id. The API does not persist/return
 * `section_name` on a line, so column-id matching — not section matching — is the
 * primary rule.
 */

/** A line's field_values as a safe array. */
function fieldValuesOf(line: any): any[] {
  return Array.isArray(line?.field_values) ? line.field_values : [];
}

/**
 * Normalize a saved line's section_name:
 *   undefined -> not present (the API never returns it),
 *   null      -> sectionless ("general") bucket,
 *   string    -> the section name.
 */
export function normalizeLineSectionName(name: any): string | null | undefined {
  if (name === undefined) {
    return undefined;
  }
  if (name === null || name === '' || name === SECTIONLESS_SECTION_NAME) {
    return null;
  }
  return name;
}

/**
 * The saved lines that belong to a table block, in saved order.
 *
 * A line qualifies ONLY when it carries at least one of the block's column ids —
 * so a question answer (custom_field id, disjoint from column ids) can never be
 * rendered as a table row. `section_name` is honoured only when a line actually
 * carries one (rows still in memory from this session), so multi-table forms
 * don't cross-claim rows.
 */
export function selectTableLines(lines: any[], columns: Field[], sectionName: string | null = null): any[] {
  const columnIds = new Set((columns || []).map(c => c?.id));
  return (Array.isArray(lines) ? lines : []).filter(line => {
    if (!fieldValuesOf(line).some(fv => columnIds.has(fv?.id))) {
      return false;
    }
    const lineSection = normalizeLineSectionName(line?.section_name);
    return lineSection === undefined ? true : lineSection === (sectionName || null);
  });
}

/**
 * Map one saved line onto `{ <column name>: value }`. Values are stored by field
 * ID and the row's form controls are keyed by field NAME — this is the bridge.
 * A column with no saved value maps to null.
 */
export function rowValuesFromLine(line: any, columns: Field[]): Record<string, any> {
  const values: Record<string, any> = {};
  const fieldValues = fieldValuesOf(line);
  (columns || []).forEach(col => {
    const matched = fieldValues.find(fv => fv?.id === col?.id);
    values[col?.name] = matched?.value ?? null;
  });
  return values;
}

/**
 * ---- Time fields (pure) ----
 *
 * Storage format is 24-hour `HH:MM:SS` (what the DB holds and what mobile
 * writes); seconds are never captured, always `00` on the way out. The UI shows
 * 12-hour `h:mm AM/PM` and the shared `app-time-select` needs two companion
 * controls alongside the value control: `<name>_timePeriod` (`hh:mm`) and
 * `<name>_am_pm` (`AM`/`PM`).
 */

/** True for a field whose control needs the time companion controls. */
export function isTimeField(field: any): boolean {
  return (field?.componentType || field?.type) === 'time';
}

/** Split any supported time string into 24-hour hours + minutes. */
function parseTimeToHm(value: any): { hours: number; minutes: number } | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const trimmed = value.trim();
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?$/.exec(trimmed);
  if (!match) {
    return null;
  }
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[4] ? match[4].toUpperCase() : null;
  if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59) {
    return null;
  }
  if (meridiem) {
    if (hours < 1 || hours > 12) {
      return null;
    }
    hours = hours % 12;
    if (meridiem === 'PM') {
      hours += 12;
    }
  } else if (hours > 23) {
    return null;
  }
  return { hours, minutes };
}

/** `'14:30:00'` (or `'14:30'` / `'2:30 PM'`) -> `'2:30 PM'`. Unparseable -> null. */
export function formatStoredTimeForDisplay(value: any): string | null {
  const hm = parseTimeToHm(value);
  if (!hm) {
    return null;
  }
  const meridiem = hm.hours >= 12 ? 'PM' : 'AM';
  const hour12 = hm.hours % 12 || 12;
  return `${hour12}:${String(hm.minutes).padStart(2, '0')} ${meridiem}`;
}

/**
 * The `app-time-select` companion values for a stored/display time:
 * `{ timePeriod: 'hh:mm' (12-hour, zero padded), amPm: 'AM' | 'PM' }`.
 */
export function timeCompanionValues(value: any): { timePeriod: string | null; amPm: string | null } {
  const hm = parseTimeToHm(value);
  if (!hm) {
    return { timePeriod: null, amPm: null };
  }
  const meridiem = hm.hours >= 12 ? 'PM' : 'AM';
  const hour12 = hm.hours % 12 || 12;
  return {
    timePeriod: `${String(hour12).padStart(2, '0')}:${String(hm.minutes).padStart(2, '0')}`,
    amPm: meridiem,
  };
}

/**
 * `'2:30 PM'` (or `'02:30 PM'` / `'14:30'`) -> `'14:30:00'` for storage. Values
 * that are already `HH:MM:SS` round-trip unchanged; unparseable input is passed
 * through untouched so nothing is silently destroyed.
 */
export function toStoredTime(value: any): any {
  const hm = parseTimeToHm(value);
  if (!hm) {
    return value ?? null;
  }
  return `${String(hm.hours).padStart(2, '0')}:${String(hm.minutes).padStart(2, '0')}:00`;
}

/**
 * The accordion heading for a saved/edited row: `Line 3 (2:30 PM, 5.5, 55)`.
 * Uses the ROW's own values (a column label would read the same on every row);
 * time columns are shown in display form and empty columns are skipped.
 */
export function buildRowSummary(values: Record<string, any>, columns: Field[], index: number): string {
  const parts: string[] = [];
  (columns || []).forEach(col => {
    const raw = values?.[col?.name];
    if (raw === null || raw === undefined || raw === '') {
      return;
    }
    parts.push(isTimeField(col) ? formatStoredTimeForDisplay(raw) || String(raw) : String(raw));
  });
  const label = `Line ${index}`;
  return parts.length ? `${label} (${parts.join(', ')})` : label;
}

const DEFAULT_RESPONSE_OPTIONS = ['Yes', 'No', 'N/A'];
const DEFAULT_SIGNATURE_ROLES = ['QA', 'QC'];
const SECTIONLESS_SECTION_NAME = 'general_questions';

/** A field's section_name is "sectionless" when absent/empty or the general bucket. */
function sectionNameOf(field: Field): string | null {
  const name = field?.ui_config?.section_name;
  if (name === undefined || name === null || name === '' || name === SECTIONLESS_SECTION_NAME) {
    return null;
  }
  return name;
}

function normalizeFields(fields: any): Field[] {
  return Array.isArray(fields) ? fields.filter(f => f != null) : [];
}

export function normalizeFormToBlocks(form: any): Block[] {
  if (!form || typeof form !== 'object') {
    return [];
  }

  const uiConfig = form.ui_config && typeof form.ui_config === 'object' ? form.ui_config : {};

  const responseOptions: string[] =
    Array.isArray(uiConfig.response_options) && uiConfig.response_options.length > 0
      ? uiConfig.response_options
      : DEFAULT_RESPONSE_OPTIONS;

  const allFields = normalizeFields(form.fields);
  const headerFields = normalizeFields(uiConfig.header && uiConfig.header.fields);
  const footerFields = normalizeFields(uiConfig.footer && uiConfig.footer.fields);
  const lineFields = normalizeFields(uiConfig.lines && uiConfig.lines.fields);
  const sections: Array<{
    name: string;
    description?: string;
    instructions?: string;
    presentation?: Presentation;
  }> = Array.isArray(uiConfig.sections) ? uiConfig.sections.filter((s: any) => s != null) : [];

  // ---- Build the named blocks keyed by token, then emit in the chosen order. ----

  // Header fields carrying a section_name are grouped into per-section header
  // blocks below; the untagged ones stay in the single leading header block (the
  // only shape forms had before grouping existed).
  const untaggedHeaderFields = headerFields.filter(f => sectionNameOf(f) === null);
  const headerBlock: Block | null =
    untaggedHeaderFields.length > 0
      ? { kind: 'header', sectionName: null, label: null, instructions: null, fields: untaggedHeaderFields }
      : null;

  // section name -> block
  const sectionBlocks = new Map<string, Block>();
  for (const section of sections) {
    const instructions = section.instructions || null;
    const questions = allFields.filter(f => sectionNameOf(f) === section.name);
    if (questions.length > 0) {
      sectionBlocks.set(section.name, {
        kind: 'questions',
        sectionName: section.name,
        label: section.description || section.name,
        instructions,
        presentation: section.presentation || 'dropdown',
        responseOptions,
        questions,
      });
      continue;
    }
    const columns = lineFields.filter(f => sectionNameOf(f) === section.name);
    if (columns.length > 0) {
      sectionBlocks.set(section.name, {
        kind: 'table',
        sectionName: section.name,
        label: section.description || section.name,
        instructions,
        presentation: section.presentation || 'accordion',
        columns,
      });
      continue;
    }
    // A section with neither questions nor columns can still group HEADER fields
    // (e.g. the Confined Space permit's "Rescue Plan" / "PPE" groups).
    const groupedHeaderFields = headerFields.filter(f => sectionNameOf(f) === section.name);
    if (groupedHeaderFields.length > 0) {
      sectionBlocks.set(section.name, {
        kind: 'header',
        sectionName: section.name,
        label: section.description || section.name,
        instructions,
        fields: groupedHeaderFields,
      });
    }
  }

  const sectionlessQuestions = allFields.filter(f => sectionNameOf(f) === null);
  const generalQuestionsBlock: Block | null =
    sectionlessQuestions.length > 0
      ? {
          kind: 'questions',
          sectionName: null,
          label: null,
          instructions: null,
          presentation: 'inline',
          responseOptions,
          questions: sectionlessQuestions,
        }
      : null;

  const sectionlessColumns = lineFields.filter(f => sectionNameOf(f) === null);
  const generalTableBlock: Block | null =
    sectionlessColumns.length > 0
      ? {
          kind: 'table',
          sectionName: null,
          label: null,
          instructions: null,
          presentation: 'inline',
          columns: sectionlessColumns,
        }
      : null;

  const signaturesBlock: Block | null = uiConfig.display_signatures
    ? {
        kind: 'signatures',
        roles:
          Array.isArray(uiConfig.signature_roles) && uiConfig.signature_roles.length > 0
            ? uiConfig.signature_roles
            : DEFAULT_SIGNATURE_ROLES,
        instructions: null,
      }
    : null;

  // Form-level fields rendered BELOW everything else (e.g. the Confined Space
  // permit's "other gases" note). No section grouping — footer fields are always
  // one flat block, stored exactly like header fields (top-level field_values).
  const footerBlock: Block | null =
    footerFields.length > 0
      ? { kind: 'footer', sectionName: null, label: null, instructions: null, fields: footerFields }
      : null;

  // ---- Ordering ----
  if (Array.isArray(uiConfig.blockOrder)) {
    const blocks: Block[] = [];
    for (const token of uiConfig.blockOrder) {
      if (token === 'header') {
        if (headerBlock) {
          blocks.push(headerBlock);
        }
      } else if (token === 'footer') {
        // blockOrder is exhaustive: without a footer token a form that opts into
        // explicit ordering would silently drop its footer fields.
        if (footerBlock) {
          blocks.push(footerBlock);
        }
      } else if (token === 'signatures') {
        if (signaturesBlock) {
          blocks.push(signaturesBlock);
        }
      } else if (token === 'general_questions') {
        if (generalQuestionsBlock) {
          blocks.push(generalQuestionsBlock);
        }
      } else if (token === 'general_table') {
        if (generalTableBlock) {
          blocks.push(generalTableBlock);
        }
      } else if (typeof token === 'string' && token.indexOf('section:') === 0) {
        const sectionName = token.slice('section:'.length);
        const block = sectionBlocks.get(sectionName);
        if (block) {
          blocks.push(block);
        }
      }
    }
    return blocks;
  }

  // Canonical order: untagged header -> sections (questions / table / header group)
  // -> sectionless questions -> sectionless table -> signatures -> footer.
  const blocks: Block[] = [];
  if (headerBlock) {
    blocks.push(headerBlock);
  }
  for (const section of sections) {
    const block = sectionBlocks.get(section.name);
    if (block) {
      blocks.push(block);
    }
  }
  if (generalQuestionsBlock) {
    blocks.push(generalQuestionsBlock);
  }
  if (generalTableBlock) {
    blocks.push(generalTableBlock);
  }
  if (signaturesBlock) {
    blocks.push(signaturesBlock);
  }
  if (footerBlock) {
    blocks.push(footerBlock);
  }
  return blocks;
}
